import { Controller } from "@hotwired/stimulus"

const PORTAL_GAP = 10
const VIEWPORT_MARGIN = 12

export default class extends Controller {
  static targets = [
    "button",
    "resetBadge",
    "meter",
    "fill",
    "dailyBase",
    "creditInline",
    "plan",
    "inlineMenu",
    "resetTemplate"
  ]
  static values = { url: String }

  connect() {
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    this.closeOnKeydown = this.closeOnKeydown.bind(this)
    this.closeOnViewportChange = this.closeOnViewportChange.bind(this)
    this.closeIfCookieModalOpen = this.closeIfCookieModalOpen.bind(this)
    this.refreshSoon = this.refreshSoon.bind(this)

    document.addEventListener("click", this.closeOnOutsideClick)
    document.addEventListener("keydown", this.closeOnKeydown)
    document.addEventListener("turbo:before-cache", this.closeOnViewportChange)
    window.addEventListener("resize", this.closeOnViewportChange)
    window.addEventListener("scroll", this.closeOnViewportChange, true)
    window.addEventListener("klavy:usage-meter-refresh", this.refreshSoon)
    window.addEventListener("klavy-agent:message-complete", this.refreshSoon)

    this.observeCookieModal()
    this.closeIfCookieModalOpen()
    this.constructor.patchFetch()
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
    document.removeEventListener("keydown", this.closeOnKeydown)
    document.removeEventListener("turbo:before-cache", this.closeOnViewportChange)
    window.removeEventListener("resize", this.closeOnViewportChange)
    window.removeEventListener("scroll", this.closeOnViewportChange, true)
    window.removeEventListener("klavy:usage-meter-refresh", this.refreshSoon)
    window.removeEventListener("klavy-agent:message-complete", this.refreshSoon)
    window.clearTimeout(this.resetHideTimer)
    window.clearTimeout(this.refreshTimer)
    this.cookieModalObserver?.disconnect()
    this.closePortal()
  }

  toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isCookieModalOpen()) {
      this.closePortal()
      return
    }

    if (this.portalType === "menu") {
      this.closePortal()
      return
    }

    if (this.expandCollapsedWorkspaceSidebar()) {
      window.requestAnimationFrame(() => this.openMenuPortal())
      return
    }

    this.openMenuPortal()
  }

  showReset() {
    if (this.isCookieModalOpen()) {
      this.closePortal()
      return
    }

    this.cancelResetHide()
    this.openResetPortal()
  }

  hideResetDelayed() {
    if (this.portalType !== "reset") return

    this.resetHideTimer = window.setTimeout(() => this.closePortal(), 150)
  }

  toggleReset(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isCookieModalOpen()) {
      this.closePortal()
      return
    }

    if (this.portalType === "reset") {
      this.closePortal()
    } else {
      this.openResetPortal()
    }
  }

  resetKeydown(event) {
    if (this.isCookieModalOpen()) {
      this.closePortal()
      return
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      event.stopPropagation()
      if (this.portalType === "reset") {
        this.closePortal()
      } else {
        this.openResetPortal()
      }
    } else if (event.key === "Escape") {
      this.closePortal()
      this.resetBadgeTarget.blur()
    }
  }

  closeOnOutsideClick(event) {
    if (!this.portal) return
    if (this.element.contains(event.target)) return
    if (this.portal?.contains(event.target)) return

    this.closePortal()
  }

  closeOnKeydown(event) {
    if (event.key !== "Escape") return
    if (!this.portal) return

    const portalType = this.portalType
    this.closePortal()
    if (portalType === "menu" && this.hasButtonTarget) this.buttonTarget.focus()
    if (portalType === "reset" && this.hasResetBadgeTarget) this.resetBadgeTarget.focus()
  }

  closeOnViewportChange() {
    this.closePortal()
  }

  openMenuPortal() {
    if (!this.hasInlineMenuTarget) return

    const portal = this.inlineMenuTarget.cloneNode(true)
    portal.hidden = false
    portal.removeAttribute("hidden")
    portal.removeAttribute("data-workspace-profile-target")
    portal.setAttribute("aria-hidden", "false")
    portal.classList.remove("ws-profile-inline-menu")
    portal.classList.add("ws-profile-portal", "ws-profile-portal--menu")
    if (this.element.classList.contains("ws-profile--editor")) {
      portal.classList.add("ws-profile-portal--editor")
    }
    if (portal.id) portal.id = `${portal.id}-portal`

    this.openPortal("menu", this.element, portal)
  }

  openResetPortal() {
    if (!this.hasResetTemplateTarget || !this.hasResetBadgeTarget) return

    const portal = this.buildPortalFromTemplate(this.resetTemplateTarget)
    if (!portal) return

    this.openPortal("reset", this.element, portal)
  }

  openPortal(type, anchor, portal) {
    if (this.isCookieModalOpen()) {
      this.closePortal()
      return
    }

    this.closePortal()
    this.portal = portal
    this.portalType = type
    this.portalAnchor = anchor
    this.element.dataset.menuOpen = type === "menu" ? "true" : "false"

    portal.style.visibility = "hidden"
    portal.style.left = "0px"
    portal.style.top = "0px"
    this.matchPortalWidthToAnchor(portal, anchor)
    document.body.appendChild(portal)

    if (type === "reset") {
      portal.addEventListener("mouseenter", () => this.cancelResetHide())
      portal.addEventListener("mouseleave", () => this.hideResetDelayed())
      this.applyPortalMeter()
    }

    this.positionPortal()
    portal.style.visibility = ""
    this.setMenuExpanded(type === "menu")
    this.setResetExpanded(type === "reset")
  }

  buildPortalFromTemplate(template) {
    const fragment = template.content?.cloneNode(true)
    return fragment?.firstElementChild || null
  }

  positionPortal() {
    if (!this.portal || !this.portalAnchor) return

    const anchorRect = this.portalAnchor.getBoundingClientRect()
    const portalRect = this.portal.getBoundingClientRect()
    const profilePortal = this.portalType === "menu" || this.portalType === "reset"
    const horizontalMargin = profilePortal ? 0 : VIEWPORT_MARGIN
    const portalGap = profilePortal ? 0 : PORTAL_GAP
    const maxLeft = Math.max(horizontalMargin, window.innerWidth - portalRect.width - horizontalMargin)
    const maxTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - portalRect.height - VIEWPORT_MARGIN)

    const preferredLeft = profilePortal ? anchorRect.left : anchorRect.right - portalRect.width
    const aboveTop = anchorRect.top - portalRect.height - portalGap
    const belowTop = anchorRect.bottom + portalGap
    const preferredTop = aboveTop >= VIEWPORT_MARGIN ? aboveTop : belowTop

    const left = this.clamp(preferredLeft, horizontalMargin, maxLeft)
    const top = this.clamp(preferredTop, VIEWPORT_MARGIN, maxTop)

    this.portal.style.left = `${Math.round(left)}px`
    this.portal.style.top = `${Math.round(top)}px`
  }

  matchPortalWidthToAnchor(portal, anchor) {
    const anchorRect = anchor.getBoundingClientRect()
    const maxWidth = Math.max(120, window.innerWidth - (VIEWPORT_MARGIN * 2))
    const width = Math.min(anchorRect.width, maxWidth)

    portal.style.width = `${Math.round(width)}px`
  }

  closePortal() {
    this.cancelResetHide()
    this.portal?.remove()
    this.portal = null
    this.portalType = null
    this.portalAnchor = null
    this.setMenuExpanded(false)
    this.element.dataset.menuOpen = "false"
    this.setResetExpanded(false)
  }

  cancelResetHide() {
    window.clearTimeout(this.resetHideTimer)
  }

  setMenuExpanded(open) {
    if (this.hasButtonTarget) this.buttonTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  setResetExpanded(open) {
    if (!this.hasResetBadgeTarget) return

    const value = open ? "true" : "false"
    this.resetBadgeTarget.dataset.popoverOpen = value
    this.resetBadgeTarget.setAttribute("aria-expanded", value)
  }

  observeCookieModal() {
    if (typeof MutationObserver === "undefined" || !document.body) return

    this.cookieModalObserver = new MutationObserver(this.closeIfCookieModalOpen)
    this.cookieModalObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["hidden", "aria-hidden"]
    })
  }

  closeIfCookieModalOpen() {
    if (!this.isCookieModalOpen()) return

    this.closePortal()
  }

  isCookieModalOpen() {
    return Boolean(document.querySelector(".cookie-consent-modal:not([hidden])"))
  }

  expandCollapsedWorkspaceSidebar() {
    const sidebar = this.element.closest("[data-workspace-sidebar]")
    if (!sidebar || sidebar.dataset.collapsed !== "true") return false

    const main = document.querySelector("[data-workspace-main]")
    const collapseButton = document.querySelector("[data-workspace-collapse]")
    sidebar.dataset.collapsed = "false"
    if (main) main.dataset.collapsed = "false"
    collapseButton?.setAttribute("aria-expanded", "true")
    try { localStorage.setItem("workspace_sidebar_collapsed_v1", "false") } catch (_) {}
    return true
  }

  refreshSoon() {
    if (!this.hasUrlValue) return

    window.clearTimeout(this.refreshTimer)
    this.refreshTimer = window.setTimeout(() => this.refresh(), 350)
  }

  async refresh() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) return

      const payload = await response.json()
      this.applyMeter(payload.usage_meter || payload)
    } catch (_) {
      // 사용량 표시는 보조 UI라서 네트워크 실패 시 조용히 유지한다.
    }
  }

  applyMeter(meter) {
    if (!meter) return

    this.currentMeter = meter
    const state = meter.state || "normal"
    const dailyUsed = Number(meter.daily_used ?? meter.used ?? 0)
    const dailyCap = Number(meter.daily_cap ?? meter.cap ?? 0)
    const dailyPercent = Number(meter.daily_percent ?? meter.percent ?? 0)

    if (this.hasMeterTarget) {
      this.meterTarget.classList.remove(
        "ws-profile-usage--normal",
        "ws-profile-usage--warning",
        "ws-profile-usage--credit",
        "ws-profile-usage--exhausted"
      )
      this.meterTarget.classList.add(`ws-profile-usage--${state}`)
    }
    if (this.hasFillTarget) this.fillTarget.style.width = `${dailyPercent}%`
    if (this.hasDailyBaseTarget) this.dailyBaseTarget.textContent = `${dailyUsed} / ${dailyCap}P`
    if (this.hasPlanTarget && meter.plan_name) this.planTarget.textContent = meter.plan_name
    if (this.hasResetBadgeTarget) {
      this.resetBadgeTarget.classList.remove(
        "ws-profile-usage__reset-badge--normal",
        "ws-profile-usage__reset-badge--warning",
        "ws-profile-usage__reset-badge--credit",
        "ws-profile-usage__reset-badge--exhausted"
      )
      this.resetBadgeTarget.classList.add(`ws-profile-usage__reset-badge--${state}`)
    }

    const creditBalance = Number(meter.credit_pack_balance || 0)
    this.updateCredit(this.creditInlineTargetOrNull, creditBalance, true)
    this.applyPortalMeter()
  }

  applyPortalMeter() {
    if (!this.portal || this.portalType !== "reset" || !this.currentMeter) return

    const dailyUsed = Number(this.currentMeter.daily_used ?? this.currentMeter.used ?? 0)
    const dailyCap = Number(this.currentMeter.daily_cap ?? this.currentMeter.cap ?? 0)
    const creditBalance = Number(this.currentMeter.credit_pack_balance || 0)
    const dailyTarget = this.portal.querySelector("[data-workspace-profile-portal-daily]")
    const creditTarget = this.portal.querySelector("[data-workspace-profile-portal-credit]")

    if (dailyTarget) dailyTarget.textContent = `${dailyUsed} / ${dailyCap}P`
    this.updateCredit(creditTarget, creditBalance, false)
  }

  updateCredit(target, creditBalance, compact) {
    if (!target) return

    target.classList.toggle("hidden", creditBalance <= 0)
    if (compact) {
      target.textContent = `+${this.compactCredit(creditBalance)}P`
      target.title = `생성 포인트 ${this.formatNumber(creditBalance)}P`
    } else {
      target.textContent = `+${this.formatNumber(creditBalance)}P`
    }
  }

  get creditInlineTargetOrNull() {
    return this.hasCreditInlineTarget ? this.creditInlineTarget : null
  }

  compactCredit(value) {
    return value > 999 ? "999" : this.formatNumber(value)
  }

  formatNumber(value) {
    try {
      return new Intl.NumberFormat("ko-KR").format(value)
    } catch (_) {
      return String(value)
    }
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  static patchFetch() {
    if (window.__workspaceProfileFetchPatched) return

    window.__workspaceProfileFetchPatched = true
    const originalFetch = window.fetch
    window.fetch = async (...args) => {
      const response = await originalFetch(...args)
      this.refreshAfterSuccessfulAiPost(args, response)
      return response
    }
  }

  static refreshAfterSuccessfulAiPost(args, response) {
    if (!response?.ok) return

    const [resource, options = {}] = args
    const url = String(typeof resource === "string" ? resource : resource?.url || "")
    const method = String(options.method || resource?.method || "GET").toUpperCase()

    if (method !== "POST") return
    if (!url.includes("/ui-ux-design/ai/") && !url.includes("/ui-planning/ai/")) return
    if (url.includes("/ui-planning/ai/") && url.includes("_stream")) return

    window.setTimeout(() => {
      window.dispatchEvent(new Event("klavy:usage-meter-refresh"))
    }, 700)
  }
}
